// User presets (§11.6): save the current look (name + groups), stored in IndexedDB, export/import as
// `.epbpreset.json`. Built-in looks (food looks, §11.18) are added in M10 through BUILT_IN_PRESETS.
import { createStore, get, set, type UseStore } from 'idb-keyval';
import { extractGroups, applyPartial } from './sync';
import { normalizeSettings } from '../model/sidecar';
import { SYNC_GROUPS, type Preset, type Settings, type SyncGroup } from '../model/types';

export const PRESET_FILE_SUFFIX = '.epbpreset.json';
const FILE_KIND = 'eat-photo-booth-preset';

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-presets', 'presets'));

/** Built-in presets register here (M10: food looks). */
export const BUILT_IN_PRESETS: Preset[] = [];

export async function listUserPresets(): Promise<Preset[]> {
  try {
    return (await get<Preset[]>('list', store())) ?? [];
  } catch {
    return [];
  }
}

async function writeAll(list: Preset[]) {
  await set('list', list, store());
}

export function makePreset(name: string, groups: SyncGroup[], from: Settings, nameEs?: string): Preset {
  return {
    id: `user-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    name: name.trim() || 'Preset',
    ...(nameEs ? { nameEs } : {}),
    builtIn: false,
    groups: [...groups],
    settings: extractGroups(from, groups),
  };
}

export async function saveUserPreset(p: Preset) {
  const list = await listUserPresets();
  await writeAll([...list.filter((x) => x.id !== p.id), p]);
}

export async function deleteUserPreset(id: string) {
  await writeAll((await listUserPresets()).filter((p) => p.id !== id));
}

export function applyPreset(dst: Settings, p: Preset): Settings {
  return applyPartial(dst, p.settings, p.groups);
}

export function presetToFile(p: Preset): string {
  return JSON.stringify({ kind: FILE_KIND, version: 1, preset: { ...p, builtIn: false } }, null, 1);
}

/** Parses and validates a `.epbpreset.json`. Unknown groups are dropped; settings normalize over defaults. */
export function presetFromFile(text: string): Preset {
  const raw = JSON.parse(text) as { kind?: string; version?: number; preset?: Partial<Preset> };
  if (raw.kind !== FILE_KIND || !raw.preset) throw new Error('not an EAT Photo Booth preset');
  if ((raw.version ?? 0) > 1) throw new Error('preset file is newer than this app');
  const p = raw.preset;
  const groups = (Array.isArray(p.groups) ? p.groups : []).filter((g): g is SyncGroup =>
    (SYNC_GROUPS as readonly string[]).includes(g),
  );
  if (!groups.length) throw new Error('preset has no settings groups');
  // Normalize the stored partial settings through the sidecar normalizer, then keep only its groups.
  const full = normalizeSettings(p.settings ?? {});
  return {
    id: `user-${Date.now().toString(36)}`,
    name: String(p.name ?? 'Preset').slice(0, 80),
    ...(p.nameEs ? { nameEs: String(p.nameEs).slice(0, 80) } : {}),
    builtIn: false,
    groups,
    settings: extractGroups(full, groups),
  };
}
